import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Send, Check, Bell } from "lucide-react";
import api, { API, authToken } from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { ExportMenu } from "../../components/ExportMenu";
import { formatDate, currency, formatApiErrorDetail } from "../../lib/format";

export default function ReefBilling() {
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/invoices");
    setInvoices(r.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const action = async (id, path, msg) => {
    try {
      await api.post(`/invoices/${id}/${path}`);
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const outstanding = invoices.filter((i) => ["sent", "overdue", "draft", "due"].includes(i.display_status)).reduce((s, i) => s + i.amount, 0);
  const collected = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-8" data-testid="reef-billing">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Independent track</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Billing</h1>
        </div>
        <ExportMenu
          list="invoices"
          params={{}}
          title="Billing"
          columns={[
            { key: "number", label: "Invoice" },
            { key: "student_name", label: "Student" },
            { key: "class_title", label: "Class" },
            { key: "amount", label: "Amount", render: (i) => currency(i.amount) },
            { key: "display_status", label: "Status" },
            { key: "due_date", label: "Due", render: (i) => formatDate(i.due_date) },
          ]}
          rows={invoices}
        />
      </div>

      <div className="grid grid-cols-2 gap-5 max-w-md">
        <div className="paper-card p-5">
          <p className="font-sans text-xs uppercase tracking-wider text-stone">Outstanding</p>
          <p className="font-mono text-2xl text-garnet mt-1">{currency(outstanding)}</p>
        </div>
        <div className="paper-card p-5">
          <p className="font-sans text-xs uppercase tracking-wider text-stone">Collected</p>
          <p className="font-mono text-2xl text-confirmed mt-1">{currency(collected)}</p>
        </div>
      </div>

      <div className="space-y-3">
        {invoices.map((inv) => (
          <div key={inv.id} className="paper-card p-5" data-testid={`reef-invoice-${inv.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-ink">{inv.number}</span>
                  <StatusMark status={inv.display_status} />
                </div>
                <p className="font-serif text-xl text-ink mt-1">{inv.student_name}</p>
                <p className="font-mono text-xs text-stone">{inv.class_title} · due {formatDate(inv.due_date)}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xl text-ink">{currency(inv.amount)}</span>
                <div className="flex gap-2">
                  <button onClick={() => window.open(`${API}/invoices/${inv.id}/pdf?auth=${authToken()}`, "_blank")} className="btn-ghost text-sm" data-testid={`reef-invoice-pdf-${inv.id}`}>
                    <Download size={15} />
                  </button>
                  {inv.status === "draft" && (
                    <button onClick={() => action(inv.id, "send", "Invoice sent.")} className="btn-outline text-sm px-3 py-1.5" data-testid={`send-invoice-${inv.id}`}>
                      <Send size={14} /> Send
                    </button>
                  )}
                  {["sent", "overdue"].includes(inv.display_status) && (
                    <>
                      <button onClick={() => action(inv.id, "remind", "Reminder sent.")} className="btn-ghost text-sm" data-testid={`remind-invoice-${inv.id}`}>
                        <Bell size={14} />
                      </button>
                      <button onClick={() => action(inv.id, "mark-paid", "Marked paid.")} className="btn-brass text-sm px-3 py-1.5" data-testid={`mark-paid-${inv.id}`}>
                        <Check size={14} /> Paid
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {invoices.length === 0 && (
          <p className="font-sans text-sm text-stone">No invoices yet. Mark an Independent class "taught", then bill it from its call sheet.</p>
        )}
      </div>
    </div>
  );
}
